import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = '29 September 2025'; // update when needed

const PrivacyPolicy: React.FC = () => (
  <main className="container">
    {/* Top mini-header with Home link */}
    <header style={{ paddingTop: '0', paddingBottom: '0.5rem' }}>
      <Link to="/" className="back-home-link" aria-label="Back to home">← Back to home</Link>
    </header>

    <article className="section" style={{ background: '#fff' }}>
      <h1>Privacy Policy</h1>
      <p><em>Last updated: {LAST_UPDATED}</em></p>

      <p>
        Sparki values your privacy. This Privacy Policy explains how we collect, use, share, and
        protect your personal information when you use our website, apps, and related services
        (the “Service”). We comply with the UK General Data Protection Regulation (“UK GDPR”).
      </p>
      <p>
        For the purposes of data protection law, Sparki acts as the data controller for personal data
        collected through the Service, except where Garages independently process Customer data in
        relation to services they provide.
      </p>

      <h2>1. Information We Collect</h2>
      <p><strong>From Customers:</strong> name, contact details, login information, vehicle details (e.g. registration), booking details, and in-app communications.</p>
      <p><strong>From Garages:</strong> business name, address, contact information, staff details, service/pricing/availability information, and in-app communications.</p>
      <p><strong>Automatically:</strong> device and log data (IP address, browser/app version), usage analytics, and cookies or similar technologies.</p>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>Provide, operate, and improve the Service.</li>
        <li>Facilitate bookings and communication between Customers and Garages.</li>
        <li>Manage accounts, provide support, and send service notifications.</li>
        <li>Ensure security, prevent fraud/abuse, and comply with legal obligations.</li>
        <li>Analyse and enhance user experience and platform performance.</li>
      </ul>
      <p>We do not sell your personal data.</p>

      <h2>3. Sharing of Information</h2>
      <ul>
        <li><strong>With other users:</strong> We share necessary info between Customers and Garages to fulfill bookings.</li>
        <li><strong>Service providers:</strong> Hosting (e.g. cloud infrastructure), analytics, push notification services,
email/SMS providers, and similar vendors under appropriate contracts.</li>
        <li><strong>Legal/Compliance:</strong> When required by law or to protect rights, safety, or the Service.</li>
        <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or reorganisation.</li>
      </ul>

      <h2>4. Legal Bases for Processing</h2>
      <ul>
        <li><strong>Contract:</strong> Processing necessary to provide the Service.</li>
        <li><strong>Legitimate interests:</strong> Service improvement, security, and fraud prevention.</li>
        <li><strong>Consent:</strong> For optional activities like marketing communications.</li>
        <li><strong>Legal obligation:</strong> Compliance with applicable laws.</li>
      </ul>

      <h2>5. Retention</h2>
      <p>
        We keep personal data only as long as necessary for the purposes above or as required by law.
        When no longer required, we delete or anonymise it securely.
      </p>
      <p>
        Typical retention periods may include retaining account data while your account is active,
        and for a reasonable period thereafter for legal, regulatory, or operational purposes.
        Booking and transaction-related data may be retained for accounting and compliance purposes.
      </p>

      <h2>6. Your Rights (UK GDPR)</h2>
      <ul>
        <li>Access your data and receive a copy.</li>
        <li>Correct inaccurate or incomplete data.</li>
        <li>Request deletion in certain circumstances.</li>
        <li>Restrict or object to processing in certain circumstances.</li>
        <li>Data portability (receive data in a portable format).</li>
        <li>Withdraw consent at any time where processing relies on consent.</li>
      </ul>
      <p>To exercise your rights, contact: <a className="footer-link" href="mailto:support@sparkiapp.com">support@sparkiapp.com</a>.</p>
      <p>
        You may also have the right to lodge a complaint with the UK Information Commissioner’s Office (ICO)
        if you believe your data protection rights have been violated.
      </p>

      <h2>7. Cookies and Tracking</h2>
      <p>
        We use cookies and similar technologies to enable core functionality (e.g. login), remember
        preferences, and analyse usage. You can control cookies via your browser settings. Some
        features may not function if cookies are disabled. If you later create a standalone Cookie
        Policy, you can link it here.
      </p>

      <h2>Push Notifications</h2>
      <p>
        If you enable notifications, we may send you service-related push notifications (e.g. booking updates,
        messages, reminders). You can control notification preferences in your device settings.
      </p>

      <h2>8. Security</h2>
      <p>
        We implement appropriate technical and organisational measures to protect personal data
        against unauthorised access, alteration, disclosure, or destruction. However, no system is
        completely secure and we cannot guarantee absolute security.
      </p>

      <h2>9. International Transfers</h2>
      <p>
        Where we transfer personal data outside the UK (e.g. to cloud providers), we use appropriate
        safeguards such as UK International Data Transfer Agreements or Standard Contractual Clauses.
      </p>

      <h2>10. Children</h2>
      <p>
        Sparki is not directed to individuals under 18. We do not knowingly collect children’s data.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Policy from time to time. Material changes will be communicated via the
        Service or email. Continued use after updates constitutes acceptance.
      </p>

      <h2>12. Contact</h2>
      <p>
        Questions about this Policy or your data? Email us at{' '}
        <a className="footer-link" href="mailto:support@sparkiapp.com">support@sparkiapp.com</a>.
        If you are a UK or EU user, you may also contact us regarding your data protection rights.
      </p>
    </article>
  </main>
);

export default PrivacyPolicy;
